import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "vbb_admin_key";
const KEY_REMEMBER = "vbb_admin_key_remember"; // localStorage version

const SR_CTOR =
  typeof window !== "undefined"
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;
const SR_SUPPORTED = !!SR_CTOR;

/**
 * /admin/ideas/capture — phone-first idea-capture mini-app.
 *
 * Design goals:
 *  - Opens straight to a big markdown notes textarea (like a Google Doc).
 *  - Single-tap Save → creates a new Idea (kind=either, status=backlog).
 *  - "Keep drafting" toggle keeps the form open with notes cleared and
 *    title bumped so you can chain-add ideas.
 *  - "Remember on this device" persists the admin key to localStorage so
 *    the PWA icon works one-tap on future opens.
 *  - Mic button uses Web Speech API to dictate notes hands-free.
 *  - Accepts shared content via Web Share Target (manifest.json) so
 *    other apps can send text/links straight into this form.
 *
 * Not part of the main admin dashboard chrome — this is a leaf page.
 */
function readKey() {
  return (
    sessionStorage.getItem(KEY_STORAGE) ||
    localStorage.getItem(KEY_REMEMBER) ||
    ""
  );
}

/** Pull shared title/text/url from the query string when this page
 *  was opened via Web Share Target or a Siri Shortcut deep-link.
 *  Returns null when nothing shared. */
function readSharedPayload() {
  if (typeof window === "undefined") return null;
  const q = new URLSearchParams(window.location.search);
  const t = q.get("shared_title") || q.get("title") || "";
  const text = q.get("shared_text") || q.get("text") || "";
  const url = q.get("shared_url") || q.get("url") || "";
  if (!t && !text && !url) return null;
  const notesPieces = [];
  if (text) notesPieces.push(text);
  if (url) notesPieces.push(url);
  return { title: t, notes: notesPieces.join("\n\n") };
}

export default function IdeaCapture() {
  const [key, setKey] = useState(() => readKey());
  const [remember, setRemember] = useState(
    () => !!localStorage.getItem(KEY_REMEMBER)
  );
  const [keyInput, setKeyInput] = useState("");

  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [kind, setKind] = useState("either");
  const [tagInput, setTagInput] = useState("");
  const [keepDrafting, setKeepDrafting] = useState(true);

  const [saveState, setSaveState] = useState("idle"); // idle|saving|saved|error
  const [errorMsg, setErrorMsg] = useState("");

  // Voice dictation
  const recognitionRef = useRef(null);
  const notesAtDictationStartRef = useRef("");
  const [listening, setListening] = useState(false);
  const [voiceError, setVoiceError] = useState("");

  // Shortcut URL copy state
  const [copied, setCopied] = useState(false);

  // Set body class so the PWA/full-screen mode gets a distinct color.
  useEffect(() => {
    document.body.classList.add("vbb-capture-body");
    return () => document.body.classList.remove("vbb-capture-body");
  }, []);

  // Consume shared-payload query params on first load and clear ONLY
  // those keys from the URL so a refresh doesn't re-populate. Preserves
  // any unrelated future flags like ?debug=1.
  useEffect(() => {
    const shared = readSharedPayload();
    if (!shared) return;
    if (shared.title) setTitle((t) => t || shared.title);
    if (shared.notes) {
      setNotes((n) => (n ? `${n}\n\n${shared.notes}` : shared.notes));
    }
    if (window.history && window.history.replaceState) {
      const url = new URL(window.location.href);
      [
        "shared_title",
        "shared_text",
        "shared_url",
        "title",
        "text",
        "url",
      ].forEach((k) => url.searchParams.delete(k));
      const clean = `${url.pathname}${url.search}${url.hash}`;
      window.history.replaceState({}, "", clean);
    }
  }, []);

  // Cleanly stop dictation if the component unmounts mid-listen.
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch {
          // no-op — already stopped
        }
      }
    };
  }, []);

  const onUnlock = () => {
    const k = keyInput.trim();
    if (!k) return;
    sessionStorage.setItem(KEY_STORAGE, k);
    if (remember) localStorage.setItem(KEY_REMEMBER, k);
    setKey(k);
    setKeyInput("");
  };

  const onToggleRemember = (checked) => {
    setRemember(checked);
    const current = key || sessionStorage.getItem(KEY_STORAGE) || "";
    if (checked && current) {
      localStorage.setItem(KEY_REMEMBER, current);
    } else if (!checked) {
      localStorage.removeItem(KEY_REMEMBER);
    }
  };

  const onLock = () => {
    sessionStorage.removeItem(KEY_STORAGE);
    localStorage.removeItem(KEY_REMEMBER);
    setKey("");
    setRemember(false);
  };

  const startDictation = () => {
    if (!SR_SUPPORTED) return;
    setVoiceError("");
    // Snapshot the notes so interim results can be layered on top
    // without clobbering what you already typed.
    notesAtDictationStartRef.current = notes;
    const rec = new SR_CTOR();
    rec.lang = navigator.language || "en-US";
    rec.continuous = true;
    rec.interimResults = true;

    rec.onresult = (event) => {
      let interim = "";
      let finalText = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const r = event.results[i];
        const chunk = r[0]?.transcript || "";
        if (r.isFinal) finalText += chunk;
        else interim += chunk;
      }
      const base = notesAtDictationStartRef.current;
      const separator = base && !base.endsWith("\n") ? "\n" : "";
      const combined = `${base}${separator}${finalText}${interim}`.trim();
      setNotes(combined);
      // Once a chunk becomes final, promote it into the "base" so the
      // next interim doesn't clobber it.
      if (finalText) {
        notesAtDictationStartRef.current =
          `${base}${separator}${finalText}`.trimEnd();
      }
    };
    rec.onerror = (event) => {
      const code = event?.error || "unknown";
      if (code === "not-allowed" || code === "service-not-allowed") {
        setVoiceError("Microphone blocked. Enable it in your browser.");
      } else if (code === "no-speech") {
        setVoiceError("Didn't hear anything — tap again to try.");
      } else {
        setVoiceError(`Voice error: ${code}`);
      }
      setListening(false);
    };
    rec.onend = () => {
      setListening(false);
    };
    recognitionRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch (err) {
      setVoiceError("Could not start dictation.");
      setListening(false);
    }
  };

  const stopDictation = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        // no-op — already stopped
      }
    }
    setListening(false);
  };

  const toggleDictation = () => {
    if (listening) stopDictation();
    else startDictation();
  };

  const copyShortcutUrl = async () => {
    const url = `${window.location.origin}/admin/ideas/capture`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Older iOS Safari / permission denied — fall back to a prompt.
      window.prompt("Copy this URL:", url);
    }
  };

  const parseTags = (raw) =>
    raw
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

  const onSave = async () => {
    if (!title.trim() && !notes.trim()) {
      setErrorMsg("Add a title or some notes first.");
      setSaveState("error");
      return;
    }
    setSaveState("saving");
    setErrorMsg("");
    try {
      const payload = {
        title: title.trim() || notes.trim().split("\n")[0].slice(0, 120),
        kind,
        notes: notes.trim(),
        tags: parseTags(tagInput),
        status: "backlog",
        source: "capture-mobile",
      };
      await axios.post(`${API}/admin/ideas`, payload, {
        headers: { "X-Admin-Key": key },
      });
      setSaveState("saved");
      if (keepDrafting) {
        // Clear notes, keep title-as-prefix so a new idea can be built.
        setNotes("");
        setTagInput("");
        setTitle("");
        setTimeout(() => setSaveState("idle"), 1400);
      } else {
        setTimeout(() => setSaveState("idle"), 1800);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        setErrorMsg("Admin key rejected. Please unlock again.");
        setKey("");
        sessionStorage.removeItem(KEY_STORAGE);
        localStorage.removeItem(KEY_REMEMBER);
      } else {
        setErrorMsg("Could not save. Try again in a moment.");
      }
      setSaveState("error");
    }
  };

  if (!key) {
    return (
      <main
        className="min-h-screen bg-white px-5 pt-16 pb-32 max-w-md mx-auto"
        data-testid="idea-capture-locked"
      >
        <div className="vbb-overline">
          <span className="vbb-dot" />
          Capture
        </div>
        <h1 className="font-display uppercase text-4xl mt-4 tracking-tighter">
          Locked.
        </h1>
        <p className="mt-4 text-[15px] text-[#2a2a2a] leading-relaxed">
          Enter your admin key to start capturing ideas.
        </p>
        <input
          type="password"
          value={keyInput}
          onChange={(e) => setKeyInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onUnlock();
          }}
          placeholder="Admin key"
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          className="mt-6 w-full border border-[#0a0a0a] p-3 text-[16px] focus:outline-none focus:border-[#e34234]"
          data-testid="capture-key-input"
        />
        <label
          className="mt-4 flex items-center gap-2 text-[14px] text-[#2a2a2a] cursor-pointer"
          data-testid="capture-remember-label"
        >
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            className="w-5 h-5"
            data-testid="capture-remember-checkbox"
          />
          Remember on this device
        </label>
        <button
          type="button"
          onClick={onUnlock}
          disabled={!keyInput.trim()}
          className="mt-6 w-full vbb-btn disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="capture-unlock-btn"
        >
          Unlock
        </button>
        <p className="mt-8 font-mono-tiny text-[11px] text-[#6b6b6b]">
          Tip: after unlocking, tap &ldquo;Add to Home Screen&rdquo; in your
          browser menu to install this as a mini-app.
        </p>
      </main>
    );
  }

  return (
    <main
      className="min-h-screen bg-white px-5 pt-6 pb-40 max-w-md mx-auto"
      data-testid="idea-capture-page"
    >
      <header className="flex items-center justify-between">
        <div className="vbb-overline">
          <span className="vbb-dot" />
          Capture
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin/ideas"
            className="font-mono-tiny text-[12px] vbb-link"
            data-testid="capture-open-list"
          >
            All ideas →
          </Link>
          <button
            type="button"
            onClick={onLock}
            className="font-mono-tiny text-[12px] text-[#6b6b6b] hover:text-[#e34234]"
            data-testid="capture-lock-btn"
          >
            Lock
          </button>
        </div>
      </header>

      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Idea title (optional — first line of notes if blank)"
        className="mt-5 w-full border-b border-[#0a0a0a] bg-transparent py-3 text-[20px] font-display placeholder:text-[#c9c9c9] focus:outline-none"
        data-testid="capture-title-input"
      />

      <div className="mt-4 relative">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={
            SR_SUPPORTED
              ? "Notes — markdown works. Tap the mic to dictate."
              : "Notes — markdown works. Paste links freely."
          }
          rows={16}
          className="w-full border border-[#0a0a0a] p-3 pr-14 text-[15px] leading-relaxed font-mono-tiny resize-y focus:outline-none focus:border-[#e34234]"
          style={{ minHeight: "44vh" }}
          data-testid="capture-notes-input"
        />
        {SR_SUPPORTED && (
          <button
            type="button"
            onClick={toggleDictation}
            aria-label={listening ? "Stop dictation" : "Start dictation"}
            aria-pressed={listening}
            className={`absolute top-2 right-2 w-11 h-11 flex items-center justify-center border transition-colors ${
              listening
                ? "bg-[#e34234] text-white border-[#e34234] animate-pulse"
                : "bg-white text-[#0a0a0a] border-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white"
            }`}
            data-testid="capture-mic-btn"
            title={listening ? "Stop dictation" : "Dictate notes"}
          >
            {/* Simple mic glyph, no font dep */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="square"
              strokeLinejoin="miter"
              aria-hidden="true"
            >
              <rect x="9" y="3" width="6" height="12" rx="3" />
              <path d="M5 11a7 7 0 0 0 14 0" />
              <line x1="12" y1="18" x2="12" y2="22" />
              <line x1="8" y1="22" x2="16" y2="22" />
            </svg>
          </button>
        )}
      </div>
      {voiceError && (
        <p
          className="mt-2 font-mono-tiny text-[11px] text-[#e34234]"
          data-testid="capture-voice-error"
        >
          {voiceError}
        </p>
      )}
      {listening && (
        <p
          className="mt-2 font-mono-tiny text-[11px] text-[#e34234]"
          data-testid="capture-listening-indicator"
        >
          ● Listening — tap the mic again to stop.
        </p>
      )}

      <div className="mt-4 flex items-center gap-2">
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          className="border border-[#0a0a0a] p-2 text-[13px] bg-white"
          data-testid="capture-kind-select"
        >
          <option value="either">Kind: undecided</option>
          <option value="story">Story</option>
          <option value="essay">Essay</option>
        </select>
        <input
          type="text"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          placeholder="tags, comma-separated"
          className="flex-1 border border-[#0a0a0a] p-2 text-[13px] font-mono-tiny focus:outline-none focus:border-[#e34234]"
          data-testid="capture-tags-input"
        />
      </div>

      <label
        className="mt-4 flex items-center gap-2 text-[13px] text-[#2a2a2a] cursor-pointer"
        data-testid="capture-keep-drafting-label"
      >
        <input
          type="checkbox"
          checked={keepDrafting}
          onChange={(e) => setKeepDrafting(e.target.checked)}
          className="w-5 h-5"
          data-testid="capture-keep-drafting-checkbox"
        />
        Keep drafting after save (clear notes, stay here)
      </label>

      <label
        className="mt-2 flex items-center gap-2 text-[13px] text-[#6b6b6b] cursor-pointer"
        data-testid="capture-remember-label"
      >
        <input
          type="checkbox"
          checked={remember}
          onChange={(e) => onToggleRemember(e.target.checked)}
          className="w-5 h-5"
          data-testid="capture-remember-checkbox"
        />
        Remember on this device
      </label>

      {/* Install / Shortcut helper */}
      <details
        className="mt-8 border border-[#c9c9c9] p-4"
        data-testid="capture-install-panel"
      >
        <summary className="cursor-pointer font-mono-tiny text-[12px] uppercase tracking-wider text-[#0a0a0a]">
          Add to home screen · make a shortcut
        </summary>
        <div className="mt-3 space-y-3 text-[13px] leading-relaxed text-[#2a2a2a]">
          <button
            type="button"
            onClick={copyShortcutUrl}
            className="vbb-btn vbb-btn-ghost w-full"
            data-testid="capture-copy-url-btn"
          >
            {copied ? "Copied ✓" : "Copy shortcut URL"}
          </button>
          <div>
            <div className="font-mono-tiny text-[11px] uppercase tracking-wider text-[#6b6b6b] mb-1">
              iOS · Siri Shortcut
            </div>
            <ol className="list-decimal pl-5 space-y-0.5 text-[12px]">
              <li>
                In Safari, tap Share &rarr; <em>Add to Home Screen</em>.
              </li>
              <li>
                Or open Shortcuts &rarr; new shortcut &rarr; <em>Open URLs</em>{" "}
                &rarr; paste the URL above &rarr; add to home screen or ask
                Siri &ldquo;capture idea&rdquo;.
              </li>
            </ol>
          </div>
          <div>
            <div className="font-mono-tiny text-[11px] uppercase tracking-wider text-[#6b6b6b] mb-1">
              Android · Chrome
            </div>
            <ol className="list-decimal pl-5 space-y-0.5 text-[12px]">
              <li>
                Menu (&#8942;) &rarr; <em>Add to Home screen</em>. Icon opens
                straight here.
              </li>
              <li>
                For a quick-tile shortcut, most launchers accept a URL
                shortcut widget — paste the copied URL.
              </li>
            </ol>
          </div>
          <p className="font-mono-tiny text-[11px] text-[#6b6b6b]">
            Bonus: this page is registered as a share target — share text
            or a link from any app and it&apos;ll open here pre-filled.
          </p>
        </div>
      </details>

      {/* Sticky save bar */}
      <div
        className="fixed bottom-0 left-0 right-0 border-t border-[#0a0a0a] bg-white px-5 py-3"
        data-testid="capture-save-bar"
      >
        <div className="max-w-md mx-auto flex items-center gap-3">
          <span
            className="font-mono-tiny text-[12px] text-[#6b6b6b] flex-1"
            data-testid="capture-save-state"
          >
            {saveState === "saving" && "Saving…"}
            {saveState === "saved" && "Saved. ✓"}
            {saveState === "error" && (
              <span className="text-[#e34234]">{errorMsg || "Error"}</span>
            )}
            {saveState === "idle" && (title || notes ? "Ready" : "Waiting")}
          </span>
          <button
            type="button"
            onClick={onSave}
            disabled={
              saveState === "saving" ||
              (!title.trim() && !notes.trim())
            }
            className="vbb-btn disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="capture-save-btn"
          >
            Save idea
          </button>
        </div>
      </div>
    </main>
  );
}
